<template>
  <v-container  ref="barWrapper" fluid fill-height  ma-0 pa-0 >
    <loading-circle
      :show="loader"
    />
    <v-layout fill-height>
      <v-flex xs12 pa-0>
        <scroll-table
          :data="getTable"
          :options="options"
          :sortable="false"
          :translate="true"
          :subscribed="true"
          :hideValues="['undefined']"
          @select="(payload) => onDeviceSelect(payload)"
        />
      </v-flex>
    </v-layout>
    <device-bar
      :open="getDeviceBarOpen"
      @update=""
      @load-devices=""
      @close="() => {this.$store.dispatch('activeDevice', null)}"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import scrollArea from '@/nms/components/partials/scrollarea.vue'
import loadingCircle from '@/nms/components/partials/loader.vue'
import LocationTable from '@/nms/components/partials/scrolltable.vue'
import scrollTable from '@/nms/components/partials/scrolltable.vue'
import DeviceBar from '@/nms/pages/device/device-bar.vue'

import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapMutations, mapActions } = createNamespacedHelpers('schemas')

// Типы для данных
interface MapModalData {
  $id?: string
  name?: string
  cls?: string
  getLinks?: (options?: any) => Promise<any[]>
  [key: string]: any
}

interface DeviceItem {
  $id?: string
  name?: string
  cls?: string
  device_type?: string
  children?: DeviceItem[]
  [key: string]: any
}

interface TableRow {
  device?: DeviceItem
  name?: string
  $id?: string
  place?: string
  cabinet?: string
  [key: string]: any
}

interface TableColumn {
  title: string
  value: string
  type: string
  width: string
}

interface TableData {
  columns: TableColumn[]
  rows: TableRow[]
}

interface OptionsColumns {
  [key: string]: { width: string }
}

interface OptionsData {
  columns: OptionsColumns
}

interface BarState {
  device: { open: boolean }
}

interface ActiveDevice {
  $id?: string
  [key: string]: any
}

export default Vue.extend({
  name: 'MAP_MODAL',

  components: {
    scrollArea,
    loadingCircle,
    LocationTable,
    scrollTable,
    DeviceBar,
  },

  props: {
    data: {
      type: Object as () => MapModalData,
      default: () => ({})
    },
  },

  data(): {
    loader: boolean
    devices: DeviceItem[]
    schema: any | null
    bar: BarState
    options: OptionsData
    filter: any | null
    search: string
    table: TableData
  } {
    return {
      loader: false,
      devices: [],
      schema: null,
      bar: {
        device: { open: false },
      },
      options: {
        columns: {
          text: { width: '40%' },
          timestamp: { width: '40%' },
          actions: { width: '40%' },
          endpoint: { width: '40%' },
          check: { width: '20%' },
        },
      },
      filter: null,
      search: '',
      table: {
        columns: [
          {
            title: 'place',
            value: 'place',
            type: 'text',
            width: '30%'
          },
          {
            title: 'cabinet',
            value: 'cabinet',
            type: 'text',
            width: '20%'
          },
          {
            title: 'device',
            value: 'name',
            type: 'text',
            width: '40%'
          },
        ],
        rows: []
      },
    }
  },

  computed: {
    getData(): MapModalData {
      return this.data
    },

    getDeviceBarOpen(): boolean {
      const dev = this.$store.state.devices.activeDevice as ActiveDevice | null
      this.bar.device.open = !!(dev && dev.$id)
      return this.bar.device.open
    },

    getTable(): TableData {
      const t = this.$merge({}, this.table) as TableData
      return t
    },

    ...mapGetters(['schemas'])
  },

  created(): void {
    this.setup()
  },

  methods: {
    ...mapActions(['openSchema']),

    async setup(): Promise<void> {
      this.loadDevices()
      this.reset()
      this.statusUnsubscribe('MAP_MODAL')
      this.prepareTable()
    },

    async loadDevices(): Promise<void> {
      try {
        this.setLoader('loadDevices', true)
        const data$ = await this.getData.getLinks?.({ compute: 'is_used' })
        this.devices = []
        
        if (data$?.length) {
          data$.forEach((child: DeviceItem) => {
            if (child.cls === 'device') {
              this.devices.push(child)
            }
          })
        }
        
        this.setLoader('loadDevices', false)
      } catch (err) {
        console.error('CATCH', this.$options.name, 'loadDevices', err)
        this.setLoader('loadDevices', false)
        this.loader = false
      }
    },

    async onDeviceSelect(payload: DeviceItem): Promise<void> {
      try {
        const query = {
          id: [payload.$id],
          notNull: true,
          data: {
            links: { flatten: false }
          }
        }
        const device$ = await this.$bridge.cmdbQuery(query)
        
        if (device$?.[0]) {
          device$[0].children = device$[0].links
          await this.$store.dispatch('activeDevice', device$[0])
        }
      } catch (err) {
        console.error('CATCH', this.$options.name, 'onDeviceSelect', err)
        this.loader = false
      }
    },

    setLoader(name: string, status: boolean): void {
      this.$store.dispatch('setLoader', {
        key: name,
        value: status
      })
    },

    async prepareTable(): Promise<void> {
      try {
        const locationTree = await this.getData.getLinks?.()
        const arr: TableRow[] = []
        const subscribeArr: DeviceItem[] = []

        const heapData = (arr$: DeviceItem[], data?: DeviceItem, parent?: string): void => {
          const parent$ = parent || this.data.name || ''
          
          arr$.forEach((child: DeviceItem) => {
            const ownParent$ = child.cls === 'location' ? `${parent$} / ${child.name}` : parent$
            const row$: TableRow = {}
            
            if (child.cls === 'device') {
              row$.device = child
              row$.name = child.name
              row$.$id = child.$id
              row$.place = ownParent$
              row$.cabinet = data?.cls === 'cabinet' ? data.name : this.$t('out of cabinet') as string
              arr.push(row$)
              subscribeArr.push(child)
            }
            
            if (child.cls === 'module' && data?.cls === 'location') {
              row$.device = child
              row$.name = child.name
              row$.$id = child.$id
              row$.place = ownParent$
              row$.cabinet = this.$t('out of device') as string
              arr.push(row$)
              subscribeArr.push(child)
            }
            
            if (child.children?.length) {
              heapData(child.children, child, ownParent$)
            }
          })
        }

        console.log(this.$options.name, 'PREPARE_TABLE locationTree', locationTree)
        
        if (locationTree) {
          heapData(locationTree, this.data)
        }
        
        console.log(this.$options.name, 'PREPARE_TABLE', arr)
        this.table.rows = arr
        this.loader = false
        this.statusSubscribe(subscribeArr, 'MAP_MODAL')
      } catch (err) {
        console.error('CATCH', this.$options.name, 'prepareTable', err)
        this.loader = false
      }
    },

    async statusSubscribe(arr: DeviceItem[], group: string): Promise<void> {
      this.$store.dispatch('measuresSubscribeInit', {
        name: 'addStatus',
        group: group,
        listener: 'status'
      })
      
      if (arr.length) {
        arr.forEach((item: DeviceItem) => {
          if (item.$id) {
            this.$store.dispatch('addStatusKey', item)
          }
        })
        await this.$bridge.subscribe(arr.map(obj => obj.$id))
      }
    },

    subscribeHandler(e: { $id?: string; [key: string]: any }): void {
      if (e.$id) {
        this.$store.dispatch('addStatus', e)
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },

    reset(): void {
      this.table.rows = []
    }
  }
})
</script>

<style scoped lang="less">
.mnemo {
  width: 100%;
  height: 100%;
  display: flex;
}
.diagram {
  width: 100%;
  height: 100%;
}
.tag-nav-wrapper {
  padding: 0;
  flex-grow: 0;
  height: 37px;

  overflow-y: visible;
}
.block-container {
  width: 100%;
  // height: 650px;
  height: 100%;
  // height: ~'calc(100% - 50px)';
  border: 1px solid black;
  padding: 0;
}
.menu {
  position: absolute;
  top: 0;
  left: 0;
  width: 400px;
  height: 100%;
  background-color: white;
  border: 1px solid black;
  padding: 10px;
  padding-top: 30px;
  z-index: 10;
}
.img {
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
}

.link {
  display: inline-block;
  color: #000;
  text-decoration: none;
}

.link::after {
  content: '';
  display: block;
  width: 0;
  height: 2px;
  background: #000;
  transition: width 0.3s;
}

.link:hover::after {
  width: 100%;
  //transition: width .3s;
}
</style>
